import { MouseEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { AddToCartRequest, PointOfPurchaseShopViewModel } from '@/types/api';
import { SupportedEvents, sendAddOrRemoveCartItemEvent } from '@/features/analytics/datalayer';
import { IconAdd } from '@/features/icons';
import SpinnerLoading from '@/features/loading/spinner-loading';
import usePostAddToCart from '@/features/product-card/utils/use-post-add-to-cart';
import { Webengage } from '@/features/webengage';
import { SupportedEvents as WESupportedEvents } from '@/features/webengage/types';
import { CartActionPlacement } from '@/features/webengage/types';
import { QueryKeys } from '@/app/_utils/react-query';
import { useAppCart } from '@/app/_utils/use-app-cart';
interface Props {
  atpId: AddToCartRequest['atpId'];
  itemProposalId: AddToCartRequest['itemProposalId'];
  size?: 'large' | 'medium' | 'responsive';
  index?: number;
}
export const DirectAddToCart = ({
  atpId,
  itemProposalId,
  size = 'responsive',
  index
}: Props) => {
  const queryClient = useQueryClient();
  const {
    updateCart,
    cartInStorage: prevCart
  } = useAppCart();
  const {
    mutateAsync,
    isPending
  } = usePostAddToCart({
    atpId,
    itemProposalId
  });
  const handleClick = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      const cart = await mutateAsync();
      const currentPOP = queryClient.getQueryData<PointOfPurchaseShopViewModel[]>(QueryKeys.Cart.POPList);
      const filteredList = currentPOP?.filter(item => !!item.atpId && item.atpId !== atpId);
      queryClient.setQueryData(QueryKeys.Cart.POPList, filteredList);
      updateCart(cart);
      if (prevCart) {
        sendAddOrRemoveCartItemEvent(SupportedEvents.add_to_cart, prevCart, cart, CartActionPlacement.pop_in_cart, index);
      }
      const addedItem = cart.items?.at(-1);
      Webengage.trackCartAction(addedItem ?? {}, CartActionPlacement.pop_in_cart, WESupportedEvents.add_to_cart, false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error adding to cart: ', error);
    }
  };
  return <button type='button' className={classNames('z-50 rounded-lg border border-primary-main bg-surface-solid-0 text-primary-main', {
    'p-2.5 [&>svg]:h-6 [&>svg]:w-6': size === 'large',
    'p-2 [&>svg]:h-4 [&>svg]:w-4': size === 'medium',
    'p-2 lg:p-2.5 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:lg:h-6 [&>svg]:lg:w-6': size === 'responsive'
  })} onClick={handleClick} data-sentry-component="DirectAddToCart" data-sentry-source-file="direct-add-to-cart.tsx">
            {isPending ? <SpinnerLoading className='text-primary-main' /> : <IconAdd />}
        </button>;
};